'use client';

import { MSWProvider } from '@/components/msw-provider';
import { ToastProvider } from '@/contexts/toast-context';
import { getQueryClient } from '@/lib/queryClient';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Flowbite } from 'flowbite-react';
import { lazy, Suspense, useEffect, useState } from 'react';
import { customTheme } from './theme';

declare global {
  interface Window {
    toggleDevtools?: () => void;
  }
}

// Lazy-loaded so the production devtools bundle only ships to the client
// when someone actually toggles it on via `window.toggleDevtools()`.
const ReactQueryDevtoolsProduction = lazy(() =>
  import('@tanstack/react-query-devtools/production').then((module) => ({
    default: module.ReactQueryDevtools,
  }))
);

export function Providers({ children }: { children: React.ReactNode }) {
  const queryClient = getQueryClient();
  const [showDevtools, setShowDevtools] = useState(false);

  useEffect(() => {
    window.toggleDevtools = () => setShowDevtools((prev) => !prev);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <Flowbite theme={{ theme: customTheme, mode: 'dark' }}>
          <MSWProvider>{children}</MSWProvider>
        </Flowbite>
      </ToastProvider>
      <ReactQueryDevtools initialIsOpen={false} />
      {showDevtools && (
        <Suspense fallback={null}>
          <ReactQueryDevtoolsProduction />
        </Suspense>
      )}
    </QueryClientProvider>
  );
}
